import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Telescope, LayoutDashboard, Inbox, BookOpen, Users, Brain, Sparkles, FileText,
  Globe2, Newspaper, LogOut, ExternalLink, Image as ImageIcon, Award, Compass, Menu, ScrollText, FormInput,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/admin/submissions", label: "Form Submissions", icon: Inbox },
      { to: "/admin/assets", label: "Assets / Media Library", icon: ImageIcon },
    ],
  },
  {
    label: "Site & Navigation",
    items: [
      { to: "/admin/c/site_settings", label: "Site Settings", icon: Sparkles },
      { to: "/admin/c/navigation", label: "Navigation / Menu", icon: Menu },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/admin/c/page_content", label: "Pages", icon: FileText },
      { to: "/admin/c/books", label: "Books", icon: BookOpen },
      { to: "/admin/c/legal_pages", label: "Legal Pages", icon: ScrollText },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/admin/c/founder", label: "Founder Profile", icon: Users },
      { to: "/admin/c/founder_recognitions", label: "Founder Recognition", icon: Award },
      { to: "/admin/c/founding_members", label: "Founding Members", icon: Users },
    ],
  },
  {
    label: "Research & Programs",
    items: [
      { to: "/admin/c/research_domains", label: "Research Domains", icon: Brain },
      { to: "/admin/c/research_questions", label: "Research Questions", icon: Compass },
      { to: "/admin/c/programs", label: "Programs", icon: Globe2 },
      { to: "/admin/c/media_items", label: "Media / Insights", icon: Newspaper },
    ],
  },
  {
    label: "Engage Forms",
    items: [
      { to: "/admin/c/form_configs", label: "Forms Configuration", icon: FormInput },
    ],
  },
];

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#4B5563]">Loading…</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const onLogout = async () => { await logout(); nav("/admin"); };

  return (
    <div className="min-h-screen flex bg-[#F8F6F1]">
      <aside className="hidden lg:flex w-72 flex-col bg-white border-r border-black/10">
        <Link to="/admin/dashboard" className="flex items-center gap-3 px-6 h-20 border-b border-black/10">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10">
            <Telescope className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.4} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-base">BSCA Admin</div>
            <div className="bsca-eyebrow text-[#9CA3AF] text-[10px]">CMS Console</div>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-6 mt-3 mb-1 bsca-eyebrow text-[#9CA3AF] text-[10px]">{group.label}</div>
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 ${
                      isActive
                        ? "border-[#D4AF37] bg-[#FBF3D5]/30 text-[#050B14]"
                        : "border-transparent text-[#4B5563] hover:bg-black/[0.02]"
                    }`
                  }
                >
                  <n.icon className="h-4 w-4" />
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-black/10 p-4 space-y-3">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#4B5563] hover:text-[#050B14]">
            <ExternalLink className="h-3 w-3" /> View site
          </a>
          <div className="text-xs text-[#9CA3AF]">{user?.email}</div>
          <button
            data-testid="btn-admin-logout"
            onClick={onLogout}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#050B14] text-white py-2.5 text-sm rounded-sm hover:bg-[#0A1120]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
